import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Globe, DollarSign, RotateCcw, Database, Settings as SettingsIcon, TrendingUp, FileText, GitCompare, AlertTriangle, Users, CheckCircle, Target, LogOut, Grid3x3, FileDown, LineChart } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Settings = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const [loading, setLoading] = React.useState(false);
  const [showManualEntry, setShowManualEntry] = React.useState(false);
  const [showWebService, setShowWebService] = React.useState(false);
  const [uploadedFile, setUploadedFile] = React.useState(null);
  const [webServiceConfig, setWebServiceConfig] = React.useState({
    url: '',
    apiKey: '',
    frequency: 'manual'
  });

  const handleLanguageChange = (lang) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('language', lang);
    toast.success(lang === 'tr' ? 'Dil güncellendi' : 'Language updated');
  };

  const handleLogout = () => {
    logout();
    toast.success(i18n.language === 'tr' ? 'Başarıyla çıkış yapıldı' : 'Logged out successfully');
    navigate('/login');
  };

  const handleFileUpload = async (file) => {
    if (!file) return;
    
    setUploadedFile(file);
    setLoading(true);
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/data/upload-excel`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success(`✅ File uploaded! ${response.data.records_imported} records imported`);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload file');
    } finally {
      setLoading(false);
    }
  };

  const handleManualDataSubmit = async () => {
    toast.success('Manual data entry saved!');
    setShowManualEntry(false);
  };

  const handleTestConnection = async () => {
    if (!webServiceConfig.url) {
      toast.error('Please enter API endpoint URL');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/data/test-webservice`, {
        url: webServiceConfig.url,
        api_key: webServiceConfig.apiKey
      });
      
      if (response.data.success) {
        toast.success('✅ Connection successful!');
      } else {
        toast.error('Connection failed');
      }
    } catch (error) {
      toast.error('Connection test failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveWebService = async () => {
    if (!webServiceConfig.url) {
      toast.error('Please enter API endpoint URL');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/data/save-webservice`, webServiceConfig);
      toast.success('Web service configuration saved!');
      setShowWebService(false);
    } catch (error) {
      toast.error('Failed to save configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleReloadData = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/settings/reload-data`);
      toast.success('Data reloaded successfully');
    } catch (error) {
      console.error('Error reloading data:', error);
      toast.error('Failed to reload data');
    } finally {
      setLoading(false);
    }
  };

  const handleResetDemo = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/settings/reset-demo`);
      toast.success('Demo reset successfully');
      window.location.reload();
    } catch (error) {
      console.error('Error resetting demo:', error);
      toast.error('Failed to reset demo');
    } finally {
      setLoading(false);
    }
  };

  const menuItems = [
    { path: '/', icon: TrendingUp, label: 'Dashboard Home', color: 'text-cyan-600', bg: 'bg-cyan-50' },
    { path: '/ai-assistant', icon: SettingsIcon, label: 'AI Assistant', color: 'text-purple-600', bg: 'bg-purple-50', badge: 'AI' },
    { path: '/s-curve', icon: LineChart, label: 'S-Curve Analysis', color: 'text-blue-600', bg: 'bg-blue-50' },
    { path: '/evm', icon: Target, label: 'EVM Dashboard', color: 'text-violet-600', bg: 'bg-violet-50' },
    { path: '/progress', icon: TrendingUp, label: 'Physical Progress', color: 'text-emerald-600', bg: 'bg-emerald-50' },
    { path: '/costs', icon: DollarSign, label: 'Cost Analysis', color: 'text-green-600', bg: 'bg-green-50' },
    { path: '/cash', icon: DollarSign, label: 'Cash Flow', color: 'text-amber-600', bg: 'bg-amber-50' },
    { path: '/manpower', icon: Users, label: 'Manpower', color: 'text-orange-600', bg: 'bg-orange-50' },
    { path: '/deviations', icon: AlertTriangle, label: 'Deviations', color: 'text-red-600', bg: 'bg-red-50' },
    { path: '/export', icon: FileDown, label: 'Export Reports', color: 'text-indigo-600', bg: 'bg-indigo-50', badge: 'PDF' }
  ];

  return (
    <div className="space-y-6 pb-6" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900" data-testid="page-title">{t('settings.title')}</h1>
        <p className="text-sm text-gray-500 mt-1">{t('settings.subtitle')}</p>
      </div>

      {/* User Profile */}
      <Card className="card-shadow bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-100">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-lg">
                {user?.name?.charAt(0).toUpperCase() || 'U'}
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">{user?.name || 'User'}</h3>
                <p className="text-sm text-gray-600">{user?.email || ''}</p>
                {user?.company && (
                  <p className="text-xs text-gray-500">{user.company}</p>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Language */}
      <Card className="card-shadow" data-testid="language-settings">
        <CardHeader>
          <CardTitle className="text-base flex items-center">
            <Globe size={18} className="mr-2" />
            {t('settings.language')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Select value={i18n.language} onValueChange={handleLanguageChange}>
            <SelectTrigger data-testid="language-selector">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="en">
                <span className="flex items-center gap-2">🇬🇧 {t('settings.english')}</span>
              </SelectItem>
              <SelectItem value="tr">
                <span className="flex items-center gap-2">🇹🇷 {t('settings.turkish')}</span>
              </SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Logout Button */}
      <Button
        onClick={handleLogout}
        variant="outline"
        className="w-full btn-touch border-red-200 text-red-600 hover:bg-red-50"
      >
        <LogOut size={16} className="mr-2" />
        {t('settings.logout')}
      </Button>

      {/* Quick Navigation */}
      <Card className="card-shadow">
        <CardHeader>
          <CardTitle className="text-base flex items-center">
            <Grid3x3 size={18} className="mr-2" />
            {t('settings.navigation')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-3">
            {menuItems.map((item) => (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className={`p-3 rounded-lg ${item.bg} flex flex-col items-center justify-center space-y-1 hover:opacity-80 transition-opacity relative`}
              >
                <item.icon size={20} className={item.color} />
                <span className="text-xs font-medium text-gray-700 text-center">{item.label}</span>
                {item.badge && (
                  <span className="absolute top-1 right-1 text-[8px] bg-blue-600 text-white px-1 rounded">
                    {item.badge}
                  </span>
                )}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Settings;
